import Api, { ApiConfig } from '@/api/base/api'

/**
 * adminapi (FastAPI Lambda) 의 PDP 옵션 API.
 *
 * 하이버 상품 수정에서 옵션 SKU 는 전량 adminapi 가 전담한다 (조회 + 일괄저장).
 * admin-api 의 판매정보 API 는 옵션이 수만 건이면 응답/요청을 감당하지 못하므로,
 * `optionBulkMode` 로 SKU 처리를 건너뛰게 하고 판매정보만 저장한다.
 *
 * 관련: docs/feature/260624-pdp-options-api.md
 * 배포 전제: VUE_APP_SERVER_ENDPOINT_ADMINAPI 환경변수 필요.
 */
class ProductOptionsApi extends Api {
  constructor(configs?: ApiConfig) {
    super({
      baseURL: `${process.env.VUE_APP_SERVER_ENDPOINT_ADMINAPI}`,
      ...configs,
    })
  }

  /**
   * [옵션 목록 조회] 팝업 편집기용 전체 응답 (페이징 없음).
   */
  getProductOptions(productNo: number, params?: { search?: string; only_errors?: boolean }) {
    return this.requestGet<Common.TApiResponseWrapper<Product.OptionListResponse>>(`/v1/products/${productNo}/options`, params)
  }

  /**
   * [기본옵션 마스터 조회] BASIC 상품의 신규 SKU 옵션값 셀렉트박스용.
   * 자율옵션(FREE)은 값 자유입력이므로 호출 불필요.
   */
  getBasicOptionMasters() {
    return this.requestGet<Common.TApiResponseWrapper<Product.BasicOptionMastersResponse>>('/v1/products/options/basic-masters')
  }

  /**
   * [판매정보 조회] 옵션 SKU 를 싣지 않는다 (개수만 내려온다).
   * admin-api 의 같은 경로 API 는 옵션을 전량 반환해 수만 건에서 타임아웃(504)이 난다.
   */
  getProductSales(productNo: number, sellerNo: number) {
    return this.requestGet<Common.TApiResponseWrapper<Product.SalesResponse>>(
      `/v1/products/${productNo}/sales`,
      { seller_no: sellerNo },
      // 대량 옵션 상품은 옵션 개수 집계가 느릴 수 있어 여유를 둔다.
      { timeout: 2 * 60 * 1000 }
    )
  }

  /**
   * [판매정보 저장] 옵션 SKU 는 건드리지 않는다 — 옵션 편집분은 putProductOptionsBulk 가 먼저 반영한다.
   */
  patchProductSales(productNo: number, sellerNo: number, data: Product.SalesUpdateRequest) {
    return this.requestPatch<Common.TApiResponseWrapper<Product.SalesUpdateResult>>(
      `/v1/products/${productNo}/sales?seller_no=${sellerNo}`,
      data,
      // 4만 옵션 상품은 판매정보 전파(번들+SKU 전체 UPDATE) + 이력 적재가 30초를 넘는다. 5분으로 확장.
      { timeout: 5 * 60 * 1000 }
    )
  }

  /**
   * [옵션 일괄저장] 편집기 버퍼(최종 목록 + 삭제 목록)를 통째로 반영한다.
   * SKU 생성/수정/삭제/순서를 서버가 청크로 처리하며, 상품 저장(PATCH /sales) 직전에 호출한다.
   *
   * 4만 건 저장은 수 분이 걸리므로 기본 타임아웃(30초)을 10분으로 늘린다.
   */
  putProductOptionsBulk(productNo: number, data: Product.OptionBulkSaveRequest) {
    return this.requestPut<Common.TApiResponseWrapper<Product.OptionBulkSaveResult>>(`/v1/products/${productNo}/options/bulk`, data, {
      timeout: 10 * 60 * 1000,
    })
  }

  /**
   * [SKU 별 재입고 알림신청 건수] 옵션편집기 목록의 "알림신청건" 표시용 —
   * 부가 정보라 목록 조회와 분리해 백그라운드로 호출한다 (실패해도 목록 사용에는 지장 없음).
   */
  getProductOptionsRestockCounts(productNo: number) {
    return this.requestGet<Common.TApiResponseWrapper<{ counts: Record<string, number> }>>(
      `/v1/products/${productNo}/options/restock-counts`
    )
  }

  /**
   * [재입고알림 여부 단독 변경] 상품 등록 직후 확정용 — v3 등록에는 이 속성 입력이 없어
   * DB 기본값('N')으로 생성되므로, 등록 화면의 선택값을 본체 + 소속 SKU 에 반영한다.
   * (수정 화면은 PATCH /sales 의 restock_notice_yn 으로 저장한다)
   */
  patchProductRestockNotice(productNo: number, sellerNo: number, restockNoticeYn: 'Y' | 'N') {
    return this.requestPatch<Common.TApiResponseWrapper<{ product_no: number; updated_count: number }>>(
      `/v1/products/${productNo}/restock-notice?seller_no=${sellerNo}`,
      { restock_notice_yn: restockNoticeYn }
    )
  }

  /**
   * [옵션 bulk 잡 상태 조회] 대량 요청이 비동기 접수된 경우의 폴링용 (PK 단건, 경량).
   */
  getProductOptionsBulkJob(productNo: number, jobId: string) {
    return this.requestGet<Common.TApiResponseWrapper<Product.OptionBulkJobStatus>>(`/v1/products/${productNo}/options/bulk-jobs/${jobId}`)
  }

  /**
   * [옵션 일괄저장 + 완료 대기] PUT 후, 서버가 비동기 접수(async_accepted)로 응답하면
   * 잡 상태를 폴링해 완료(SUCCESS)까지 기다린다. 동기 처리(임계치 이하)면 즉시 반환.
   *
   * 대량 저장이 CloudFront/ALB 타임아웃(30~60초)을 넘어 502 나던 문제의 프론트 측 대응 —
   * 긴 요청 1개 대신 "접수(수 초) + 짧은 폴링 반복" 으로 완료를 감지한다.
   *
   * @param onProgress 진행률 콜백 (processed, total) — 로딩 오버레이 문구 갱신용
   * @returns 최종 저장 결과 (동기/비동기 동일 형태)
   * @throws Error 잡 실패(FAILED)·폴링 상한 초과 시 사유 메시지
   */
  async putProductOptionsBulkAndWait(
    productNo: number,
    data: Product.OptionBulkSaveRequest,
    onProgress?: (processed: number, total: number) => void
  ): Promise<Product.OptionBulkSaveResult> {
    const res = await this.putProductOptionsBulk(productNo, data)
    const accepted = res.data?.data
    if (!accepted?.async_accepted || !accepted.job_id) {
      return accepted as Product.OptionBulkSaveResult
    }

    const POLL_INTERVAL_MS = 3000
    const POLL_LIMIT_MS = 20 * 60 * 1000 // 처리 자체는 서버가 계속 하므로 프론트 대기만 제한
    const MAX_POLL_ERRORS = 5 // 일시 네트워크 오류 허용 — 잡은 서버에서 계속 돈다
    const startedAt = Date.now()
    let pollErrors = 0

    for (;;) {
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS))
      if (Date.now() - startedAt > POLL_LIMIT_MS) {
        throw new Error('옵션 저장 처리가 지연되고 있습니다. 잠시 후 상품을 다시 열어 반영 여부를 확인해주세요.')
      }

      let job: Product.OptionBulkJobStatus | undefined
      try {
        const jobRes = await this.getProductOptionsBulkJob(productNo, accepted.job_id)
        job = jobRes.data?.data
        pollErrors = 0
      } catch (e) {
        pollErrors += 1
        if (pollErrors >= MAX_POLL_ERRORS) throw e
        continue
      }
      if (!job) continue

      if (job.status === 'SUCCESS') {
        return job.result ?? ({ created: 0, updated: 0, deleted: 0, ordered: 0, first_product_id: 0 } as Product.OptionBulkSaveResult)
      }
      if (job.status === 'FAILED') {
        throw new Error(job.error_message || '옵션 저장에 실패했습니다. 다시 시도해주세요.')
      }
      onProgress?.(job.processed_rows, job.total_rows)
    }
  }
}

export default ProductOptionsApi
